import React from 'react';
import { useTranslation } from 'react-i18next';

import { AccountIcon, TrashIcon } from 'src/components/icons';
import { Action } from 'src/legacy/components/Dropdowns';

import { ChannelDeleteConfirmationModal } from './ChannelDeleteConfirmationModal';
import { ChannelType } from './useChannelForm';

interface UseGroupChannelActionsOptions {
  channelType: ChannelType;
  enableActions: boolean;
  onEditClick?: () => void;
  onDeleteModalSuccess?: () => void;
  isCompanyChannel?: boolean;
  isGroupChannel?: boolean;
  disableDeletion?: boolean;
}

export enum DeleteActionLabels {
  Messaging = 'Delete conversation',
  Files = 'Delete file channel',
  Apps = 'Delete channel',
}

export const useGroupChannelActions = (
  options: UseGroupChannelActionsOptions,
) => {
  const { t } = useTranslation();
  const [deleteChannelModalOpen, setDeleteChannelModalOpen] =
    React.useState(false);

  const [groupChannelActions, setGroupChannelActions] = React.useState<
    Array<Action>
  >([]);

  const deleteActionLabel = (type: ChannelType) => {
    switch (type) {
      case 'messaging':
        return DeleteActionLabels.Messaging;
      case 'fileChannel':
        return DeleteActionLabels.Files;
      case 'extension':
        return DeleteActionLabels.Apps;
      default:
        return '';
    }
  };

  React.useEffect(() => {
    const actions = [];
    if (
      options.enableActions &&
      (options.isGroupChannel || options.isCompanyChannel)
    ) {
      actions.push({
        name: 'Edit members',
        icon: <AccountIcon />,
        onClick: () => {
          if (options.onEditClick) {
            options.onEditClick();
          }
        },
        isDisabled: options.isCompanyChannel,
        tooltipLabel: options.isCompanyChannel
          ? t(
              ($) =>
                $.validation.channels.cannot_edit_members_of_company_channel,
            )
          : '',
        isDelete: false,
        anchorElRequired: true, // for actions which require to display dropdown/popovers components set `anchorElRequired` true
      });
    }

    if (options.enableActions) {
      actions.push({
        name: deleteActionLabel(options.channelType),
        icon: <TrashIcon />,
        onClick: () => {
          // implement action handler here
          setDeleteChannelModalOpen(true);
        },
        isDelete: true,
        anchorElRequired: false,
        isDisabled: options.disableDeletion,
        tooltipLabel: options.disableDeletion
          ? t(($) => $.validation.channels.clients_must_be_part_of_channels)
          : '',
      });
    }

    setGroupChannelActions(actions);
  }, [
    options.channelType,
    options.enableActions,
    options.onEditClick,
    options.isCompanyChannel,
    options.isGroupChannel,
    options.disableDeletion,
  ]);

  const DeleteConfirmationModal = () =>
    deleteChannelModalOpen && options.enableActions ? (
      <ChannelDeleteConfirmationModal
        channelType={options.channelType}
        onClose={() => {
          setDeleteChannelModalOpen(false);
        }}
        onSuccess={() => {
          setDeleteChannelModalOpen(false);
          if (options.onDeleteModalSuccess) {
            options.onDeleteModalSuccess();
          }
        }}
      />
    ) : null;

  return { groupChannelActions, DeleteConfirmationModal };
};
